import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import BarChart from '../charts/BarChart';
import CountUp from '../charts/CountUp';
import {
  CrossFilterProvider,
  Filter,
  filterOf,
  useCrossFilter,
} from '../charts/CrossFilter';
import Donut from '../charts/Donut';
import Gauge from '../charts/Gauge';
import Heatmap from '../charts/Heatmap';
import LineChart from '../charts/LineChart';
import Radar from '../charts/Radar';
import Sankey from '../charts/Sankey';
import Sparkline from '../charts/Sparkline';
import TooltipHost from '../charts/Tooltip';
import ExportModal from './ExportModal';

type Metrics = {
  project: any;
  stats: {
    docs_total: number;
    docs_validated: number;
    docs_pending: number;
    tasks_open: number;
    tasks_done: number;
    urgent_items: number;
    captures_count: number;
    members: number;
  };
  docs: any[];
  tasks: any[];
  members: any[];
  captures: any[];
  statusCounts: Record<string, number>;
  perAssignee: Array<{ name: string; open: number; done: number; total: number }>;
  weekly: Array<{ wk: string; n: number }>;
  heatmap: Array<{ date: string; n: number }>;
  sankey: Array<{ source: string; target: string; value: number }>;
  checklist: { total: number; validated: number; ratio: number };
  radar: Array<{ axis: string; value: number }>;
  sparkWeeks: Array<{ wk: string; docs: number; tasks: number; activity: number }>;
  activity: any[];
  generated_at: string;
};

const STATUS_LABEL: Record<string, string> = {
  a_consulter: 'À consulter',
  a_traiter: 'À revoir',
  valide: 'Validé',
  archive: 'Archivé',
};

const STATUS_COLOR: Record<string, string> = {
  a_consulter: '#D48C3E',
  a_traiter: '#E07A5F',
  valide: '#8CB369',
  archive: '#A9A193',
};

export default function SynthPilotage({ projectId }: { projectId: number }) {
  return (
    <CrossFilterProvider>
      <PilotageInner projectId={projectId} />
      <TooltipHost />
    </CrossFilterProvider>
  );
}

function PilotageInner({ projectId }: { projectId: number }) {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [narrative, setNarrative] = useState<string | null>(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  async function exportPdfPilotage() {
    setExportOpen(false);
    document.documentElement.classList.add('print-pilotage-only');
    await new Promise((r) => setTimeout(r, 80));
    try {
      window.print();
    } finally {
      setTimeout(() => {
        document.documentElement.classList.remove('print-pilotage-only');
      }, 200);
    }
  }

  useEffect(() => {
    setErr(null);
    api
      .get<Metrics>(`/projects/${projectId}/synthesis/metrics`)
      .then(setMetrics)
      .catch((e) => setErr(String(e.message ?? e)));
  }, [projectId]);

  const cf = useCrossFilter();

  // Application des filtres aux listes brutes
  const filteredDocs = useMemo(() => {
    if (!metrics) return [];
    const st = filterOf(cf.filters, 'status');
    const as = filterOf(cf.filters, 'assignee');
    const cr = filterOf(cf.filters, 'creator');
    const dt = filterOf(cf.filters, 'date');
    return (metrics.docs ?? []).filter((d: any) => {
      if (st && d.status !== st.value) return false;
      if (as && (d.assigned_name ?? 'Non assigné') !== as.value) return false;
      if (cr && d.creator_name !== cr.value) return false;
      if (dt && !(d.created_at as string).startsWith(dt.value)) return false;
      if (cf.timeRange) {
        const ts = new Date((d.created_at as string).replace(' ', 'T') + 'Z').getTime();
        const lo = new Date(cf.timeRange[0] + 'T00:00:00Z').getTime();
        const hi = new Date(cf.timeRange[1] + 'T23:59:59Z').getTime();
        if (ts < lo || ts > hi) return false;
      }
      return true;
    });
  }, [metrics, cf.filters, cf.timeRange]);

  const filteredTasks = useMemo(() => {
    if (!metrics) return [];
    const as = filterOf(cf.filters, 'assignee');
    return (metrics.tasks ?? []).filter((t: any) => {
      if (as && (t.assigned_name ?? 'Non assigné') !== as.value) return false;
      if (cf.timeRange) {
        const ts = new Date((t.created_at as string).replace(' ', 'T') + 'Z').getTime();
        const lo = new Date(cf.timeRange[0] + 'T00:00:00Z').getTime();
        const hi = new Date(cf.timeRange[1] + 'T23:59:59Z').getTime();
        if (ts < lo || ts > hi) return false;
      }
      return true;
    });
  }, [metrics, cf.filters, cf.timeRange]);

  const filteredActivity = useMemo(() => {
    if (!metrics) return [];
    return (metrics.activity ?? []).filter((a: any) => {
      if (cf.timeRange) {
        const ts = new Date((a.created_at as string).replace(' ', 'T') + 'Z').getTime();
        const lo = new Date(cf.timeRange[0] + 'T00:00:00Z').getTime();
        const hi = new Date(cf.timeRange[1] + 'T23:59:59Z').getTime();
        if (ts < lo || ts > hi) return false;
      }
      return true;
    });
  }, [metrics, cf.timeRange]);

  // Données dérivées post-filtrage
  const donutData = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of filteredDocs) counts[d.status] = (counts[d.status] ?? 0) + 1;
    return Object.entries(counts).map(([k, v]) => ({
      key: k,
      label: STATUS_LABEL[k] ?? k,
      value: v,
      color: STATUS_COLOR[k] ?? 'var(--c-sage)',
    }));
  }, [filteredDocs]);

  const perAssignee = useMemo(() => {
    const m: Record<string, { name: string; open: number; done: number }> = {};
    for (const t of filteredTasks) {
      const name = t.assigned_name ?? 'Non assigné';
      if (!m[name]) m[name] = { name, open: 0, done: 0 };
      if (t.status === 'termine') m[name].done++;
      else m[name].open++;
    }
    return Object.values(m).sort(
      (a, b) => b.open + b.done - (a.open + a.done)
    );
  }, [filteredTasks]);

  const weekly = useMemo(() => {
    const m: Record<string, number> = {};
    for (const a of filteredActivity) {
      const d = new Date((a.created_at as string).replace(' ', 'T') + 'Z');
      const dow = (d.getUTCDay() + 6) % 7;
      const mon = new Date(d);
      mon.setUTCDate(d.getUTCDate() - dow);
      const wk = mon.toISOString().slice(0, 10);
      m[wk] = (m[wk] ?? 0) + 1;
    }
    return Object.entries(m)
      .map(([wk, n]) => ({ wk, n }))
      .sort((a, b) => a.wk.localeCompare(b.wk));
  }, [filteredActivity]);

  const heatmap = useMemo(() => {
    if (!metrics) return [];
    // Recompute heat from filtered activity to reflect cross-filter
    const m: Record<string, number> = Object.fromEntries(
      (metrics.heatmap ?? []).map((h) => [h.date, 0])
    );
    for (const a of filteredActivity) {
      const k = (a.created_at as string).slice(0, 10);
      if (k in m) m[k]++;
    }
    return Object.entries(m).map(([date, n]) => ({ date, n }));
  }, [metrics, filteredActivity]);

  if (err) return <div className="card error-msg">Erreur : {err}</div>;
  if (!metrics)
    return (
      <div className="pilot-stats">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="stat-card pulse-loading" style={{ height: 90 }} />
        ))}
      </div>
    );

  const stats = metrics.stats ?? {
    docs_total: 0,
    docs_validated: 0,
    docs_pending: 0,
    tasks_open: 0,
    tasks_done: 0,
    urgent_items: 0,
    captures_count: 0,
    members: 0,
  };
  const filteredCount = filteredDocs.length;
  const checklistRatio = filteredCount
    ? filteredDocs.filter((d) => d.status === 'valide').length / filteredCount
    : metrics.checklist?.ratio ?? 0;

  // Plage temporelle pour le slider : 90 derniers jours (heatmap dates)
  const timelineDates = (metrics.heatmap ?? []).map((h) => h.date);
  const firstD = timelineDates[0];
  const lastD = timelineDates[timelineDates.length - 1];
  const sparkWeeks = metrics.sparkWeeks ?? [];
  const radar = metrics.radar ?? [];
  const sankey = metrics.sankey ?? [];

  async function handleNarrative() {
    if (!metrics) return;
    setNarrativeLoading(true);
    setNarrative(null);
    try {
      const r = await api.post<{ narrative: string }>(
        `/projects/${projectId}/synthesis/narrative`,
        {
          metrics: {
            stats: metrics.stats,
            statusCounts: metrics.statusCounts,
            perAssignee: metrics.perAssignee,
            radar: metrics.radar,
            weekly: metrics.weekly,
            sankey: metrics.sankey,
            checklist: metrics.checklist,
          },
          audience: 'synthese',
        }
      );
      setNarrative(r.narrative);
    } catch (e: any) {
      setNarrative(`Erreur IA : ${e.message ?? e}`);
    } finally {
      setNarrativeLoading(false);
    }
  }

  async function saveAsTemplate() {
    setSavingTpl(true);
    try {
      await api.post(`/projects/${projectId}/synthesis`, {
        kind: 'pilotage',
        title: `Gabarit Pilotage ${new Date().toLocaleDateString('fr-CH')}`,
        payload: { metrics, filters: cf.filters, timeRange: cf.timeRange },
        is_template: true,
      });
      alert('Sauvegardé comme gabarit.');
    } finally {
      setSavingTpl(false);
    }
  }

  return (
    <>
      <div className="card" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong style={{ fontSize: 14 }}>📈 Pilotage — dashboard temps réel</strong>
        <span className="card-meta" style={{ fontSize: 11, marginRight: 'auto' }}>
          Généré {new Date(metrics.generated_at).toLocaleString('fr-CH')} · Tout est inter-connecté :
          clique sur un graphique pour filtrer les autres
        </span>
        <button className="btn btn-ghost" onClick={handleNarrative} disabled={narrativeLoading}>
          ✍ {narrativeLoading ? 'Génération…' : 'Générer le récit'}
        </button>
        <button className="btn btn-ghost" onClick={saveAsTemplate} disabled={savingTpl}>
          📦 Sauver en gabarit
        </button>
        <button className="btn btn-primary" onClick={() => setExportOpen(true)}>
          ⬇ Exporter
        </button>
      </div>

      <div ref={exportRef} data-pilotage-export>

      {cf.filters.length > 0 && (
        <div className="cross-filter-chips">
          {cf.filters.map((f: Filter, i: number) => (
            <span key={i} className="cf-chip">
              {f.kind === 'status' ? 'Statut' : f.kind === 'assignee' ? 'Assigné' : f.kind === 'creator' ? 'Créateur' : 'Date'}
              : {f.label}
              <button onClick={() => cf.clearOne(f)}>×</button>
            </span>
          ))}
          <button className="cf-chip" style={{ background: 'var(--c-muted)' }} onClick={cf.clear}>
            Tout effacer
          </button>
        </div>
      )}

      <div className="pilot-stats">
        <StatCard
          label="Documents"
          value={filteredCount}
          sparkValues={sparkWeeks.map((w) => w.docs)}
          highlight={stats.docs_validated}
          highlightLabel="validés"
        />
        <StatCard
          label="Tâches ouvertes"
          value={filteredTasks.filter((t) => t.status !== 'termine').length}
          sparkValues={sparkWeeks.map((w) => w.tasks)}
        />
        <StatCard
          label="Activité"
          value={filteredActivity.length}
          sparkValues={sparkWeeks.map((w) => w.activity)}
        />
        <StatCard
          label="Urgent / critique"
          value={
            filteredDocs.filter((d) => d.urgency === 'urgent' || d.urgency === 'critique').length +
            filteredTasks.filter((t) => t.urgency === 'urgent' || t.urgency === 'critique').length
          }
          color="var(--c-terracotta)"
        />
        <StatCard label="Captures 3D" value={stats.captures_count} color="var(--c-sage)" />
        <StatCard label="Membres" value={stats.members} />
      </div>

      <div className="pilot-grid">
        <div className="pilot-card col-4">
          <div className="pc-head">
            <span className="pc-title">Documents par statut</span>
            <span className="pc-sub">Clique pour filtrer</span>
          </div>
          <div className="pc-chart-wrap">
            <Donut
              data={donutData}
              size={200}
              thickness={42}
              centerLabel="total"
              centerValue={filteredCount}
              legend="bottom"
              activeKey={filterOf(cf.filters, 'status')?.value ?? null}
              onClickSlice={(d) =>
                cf.toggle({ kind: 'status', value: d.key, label: d.label })
              }
            />
          </div>
        </div>

        <div className="pilot-card col-4">
          <div className="pc-head">
            <span className="pc-title">Tâches par membre</span>
            <span className="pc-sub">Ouvertes / terminées</span>
          </div>
          <div className="pc-chart-wrap">
            <BarChart
              data={perAssignee.map((p) => ({ label: p.name, open: p.open, done: p.done }))}
              series={[
                { key: 'open', label: 'Ouvertes', color: '#E07A5F' },
                { key: 'done', label: 'Terminées', color: '#8CB369' },
              ]}
              stacked
              activeLabel={filterOf(cf.filters, 'assignee')?.value ?? null}
              onClickBar={(d) =>
                cf.toggle({ kind: 'assignee', value: d.label as string, label: d.label as string })
              }
            />
          </div>
        </div>

        <div className="pilot-card col-4">
          <div className="pc-head">
            <span className="pc-title">Avancement global</span>
            <span className="pc-sub">{Math.round(checklistRatio * 100)}%</span>
          </div>
          <div className="pc-chart-wrap" style={{ flexDirection: 'column' }}>
            <Gauge value={Math.round(checklistRatio * 100)} max={100} label="docs validés / total" />
            <div className="card-meta" style={{ fontSize: 11, marginTop: 8 }}>
              {filteredDocs.filter((d) => d.status === 'valide').length} / {filteredCount}
            </div>
          </div>
        </div>

        <div className="pilot-card col-8">
          <div className="pc-head">
            <span className="pc-title">Activité hebdomadaire</span>
            <span className="pc-sub">Nb événements par semaine</span>
          </div>
          <div className="pc-chart-wrap">
            {weekly.length > 0 ? (
              <LineChart
                data={weekly}
                xKey="wk"
                yKey="n"
                xLabelFormat={(v) => v.slice(5)}
                color="#587D62"
              />
            ) : (
              <div className="card-meta">Aucune activité.</div>
            )}
          </div>
        </div>

        <div className="pilot-card col-4">
          <div className="pc-head">
            <span className="pc-title">KPIs projet</span>
            <span className="pc-sub">/100</span>
          </div>
          <div className="pc-chart-wrap">
            <Radar data={radar} max={100} />
          </div>
        </div>

        <div className="pilot-card col-6">
          <div className="pc-head">
            <span className="pc-title">Flux des documents</span>
            <span className="pc-sub">Créateur → statut</span>
          </div>
          <div className="pc-chart-wrap">
            {sankey.length > 0 ? (
              <Sankey links={sankey} width={520} height={240} />
            ) : (
              <div className="card-meta">Aucun document.</div>
            )}
          </div>
        </div>

        <div className="pilot-card col-6">
          <div className="pc-head">
            <span className="pc-title">Heatmap de contribution</span>
            <span className="pc-sub">3 derniers mois · clic pour filtrer</span>
          </div>
          <div className="pc-chart-wrap" style={{ overflowX: 'auto' }}>
            <Heatmap
              data={heatmap}
              activeDate={filterOf(cf.filters, 'date')?.value ?? null}
              onClickDay={(d) => cf.toggle({ kind: 'date', value: d.date, label: d.date })}
            />
          </div>
        </div>

        <div className="pilot-card col-12">
          <div className="pc-head">
            <span className="pc-title">Documents — détail</span>
            <span className="pc-sub">Réagit au filtrage</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="slide-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Statut</th>
                  <th>Assigné</th>
                  <th>Urgence</th>
                  <th>Échéance</th>
                  <th>Versions</th>
                  <th>Sparkline</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.slice(0, 25).map((d: any) => (
                  <tr key={d.id}>
                    <td style={{ maxWidth: 280 }}>{d.title}</td>
                    <td>
                      <span
                        style={{
                          background: STATUS_COLOR[d.status] ?? 'var(--c-muted)',
                          color: '#fff',
                          padding: '1px 8px',
                          borderRadius: 100,
                          fontSize: 10,
                        }}
                      >
                        {STATUS_LABEL[d.status] ?? d.status}
                      </span>
                    </td>
                    <td>{d.assigned_name ?? '—'}</td>
                    <td>
                      {d.urgency !== 'normal' && (
                        <span
                          style={{
                            color: d.urgency === 'critique' ? '#b84b2a' : '#D48C3E',
                            fontWeight: 600,
                          }}
                        >
                          {d.urgency}
                        </span>
                      )}
                    </td>
                    <td>{d.deadline ?? '—'}</td>
                    <td>{d.versions_count}</td>
                    <td>
                      <Sparkline
                        values={Array.from({ length: 6 }, () => Math.random() * (d.versions_count + 1))}
                        width={90}
                        height={20}
                      />
                    </td>
                  </tr>
                ))}
                {filteredDocs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="card-meta" style={{ textAlign: 'center' }}>
                      Aucun document ne correspond aux filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {filteredDocs.length > 25 && (
              <div className="card-meta" style={{ padding: 8, fontSize: 11 }}>
                {filteredDocs.length - 25} lignes supplémentaires masquées.
              </div>
            )}
          </div>
        </div>
      </div>

      {firstD && lastD && (
        <TimelineSlider
          first={firstD}
          last={lastD}
          value={cf.timeRange}
          onChange={cf.setTimeRange}
        />
      )}

      {narrative !== null && (
        <div className="card" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
          <div className="card-title">🧠 Récit IA</div>
          <div>{narrative}</div>
        </div>
      )}
      </div>

      {exportOpen && (
        <ExportModal
          projectId={projectId}
          title="Pilotage"
          kind="pilotage"
          payload={{ metrics, filters: cf.filters, timeRange: cf.timeRange, narrative }}
          onClose={() => setExportOpen(false)}
          onPdf={exportPdfPilotage}
        />
      )}
    </>
  );
}

function StatCard({
  label,
  value,
  sparkValues,
  color = 'var(--c-sage)',
  highlight,
  highlightLabel,
}: {
  label: string;
  value: number;
  sparkValues?: number[];
  color?: string;
  highlight?: number;
  highlightLabel?: string;
}) {
  return (
    <div className="stat-card">
      <div className="deco" />
      <div className="lbl">{label}</div>
      <div className="val">
        <CountUp value={value} />
      </div>
      {highlight !== undefined && (
        <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 3 }}>
          <CountUp value={highlight} /> {highlightLabel}
        </div>
      )}
      {sparkValues && sparkValues.length > 0 && (
        <div className="spark">
          <Sparkline values={sparkValues} stroke={color} width={150} height={28} />
        </div>
      )}
    </div>
  );
}

function TimelineSlider({
  first,
  last,
  value,
  onChange,
}: {
  first: string;
  last: string;
  value: [string, string] | null;
  onChange: (v: [string, string] | null) => void;
}) {
  const a = new Date(first + 'T00:00:00Z').getTime();
  const b = new Date(last + 'T00:00:00Z').getTime();
  const days = Math.max(1, Math.round((b - a) / 86400_000));
  const v0 = value ? Math.round((new Date(value[0]).getTime() - a) / 86400_000) : 0;
  const v1 = value ? Math.round((new Date(value[1]).getTime() - a) / 86400_000) : days;

  return (
    <div className="pilot-timeline-slider">
      <span className="range-label">📅 Plage temporelle</span>
      <input
        type="range"
        min={0}
        max={days}
        value={v0}
        onChange={(e) => {
          const nv = Number(e.target.value);
          const lo = new Date(a + nv * 86400_000).toISOString().slice(0, 10);
          const hi = value ? value[1] : last;
          onChange([lo, hi]);
        }}
      />
      <input
        type="range"
        min={0}
        max={days}
        value={v1}
        onChange={(e) => {
          const nv = Number(e.target.value);
          const hi = new Date(a + nv * 86400_000).toISOString().slice(0, 10);
          const lo = value ? value[0] : first;
          onChange([lo, hi]);
        }}
      />
      <span className="card-meta" style={{ fontSize: 11, minWidth: 160 }}>
        {value ? `${value[0]} → ${value[1]}` : `${first} → ${last}`}
      </span>
      {value && (
        <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => onChange(null)}>
          Réinitialiser
        </button>
      )}
    </div>
  );
}
